import React, { useEffect, useMemo, useState } from 'react';
import Table, { TFilter } from '@components/table';
import styles from './styles.module.scss';
import Status from '@components/common/status';
import PriceStatus from '@components/common/price_status';
import Typography from '@components/common/typography';
import RangeDatePicker from '@components/range_date_picker';
import Button from '@components/common/button';
import Drawer from '@components/drawer';
import Input from '@components/common/input';
import Accordion from '@components/common/accordin';
import MultiSelect from '@components/common/multi_select';
import Range from '@components/range';
import {
    ETransactionAction,
    ETransactionMethod,
    ETransactionStatus,
    ETransactionType,
    TTransactionLite
} from '@xeppt/xeppt-sdk/types/transaction';
import moment from 'moment/moment';
import {
    downloadFileFromBlob,
    formatDataToXlsx,
    getTransactionAction,
    getTransactionMethod,
    getTransactionStatus,
    getTransactionStatusColor
} from '@utils/index';
import { useTransactions } from '@hooks/api/useTransactions';
import { useDebounceValue } from 'usehooks-ts';
import { transactionsMethods, transactionsStatuses, transactionsType } from '@const/index';
import { routes } from '@const/routes';
import Link from '@components/common/link';
import Dropdown from '@components/common/dropdown';
import { getDisableFeature } from '@utils/date';
import { modalIds } from '@const/modals';
import useModalContext from '@hooks/context/useModalContext';
import useDebounce from '@hooks/helpers/useDebounce';

const TransactionsLayout = () => {
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [isTransactionDrawerOpen, setIsTransactionDrawerOpen] = useState(false);
    const [dateFilter, setDateFilter] = useState<string>();

    const {
        filter,
        onChangeFilter,
        transactions,
        transactionsData,
        refetchTransactions,
        transaction,
        getSingleTransaction,
        handleRemoveTransactionData,
        handleOpenRefundModal
    } = useTransactions({ isInitialTransactions: false });

    const [search, setSearch] = useState('');
    const [requestSearch, setRequestSearch] = useDebounceValue('', 500);
    const [rangeValue, setRangeValue] = useState({
        amount_from: 0,
        amount_to: 1000
    });
    const debouncedRange = useDebounce(rangeValue, 300);

    const canBeRefunded = (transaction: TTransactionLite) => {
        const methods = [ETransactionMethod.WALLET];
        const actions = [ETransactionAction.TRANSFER, ETransactionAction.REQUEST];
        return (
            transaction?.status === ETransactionStatus.APPROVED &&
            methods.includes(transaction?.method) &&
            actions.includes(transaction?.action) &&
            transaction?.type === ETransactionType.CREDIT
        );
    };

    useEffect(() => {
        onChangeFilter(debouncedRange);
    }, [debouncedRange]);

    const columns = [
        {
            label: 'Date and Time',
            key: 'created_at',
            isSort: true,
            render: (value: string) => moment(value).format('MM.DD.YYYY HH:mm')
        },
        {
            label: 'Transaction ID',
            key: 'id'
        },
        {
            label: 'Transaction Method',
            key: 'method',
            render: (value: ETransactionMethod) => getTransactionMethod(value)
        },
        {
            label: 'Consumer Name',
            key: 'consumer_name',
            render: (value: string, data: TTransactionLite) => (
                <Link to={`${routes.consumers}/${data.consumer_id}`}>{value}</Link>
            )
        },
        {
            label: 'Action',
            key: 'action',
            render: (_: number, data: TTransactionLite) => getTransactionAction(data.action)
        },
        {
            label: 'Status',
            key: 'status',
            render: (value: ETransactionStatus) => (
                <Status variant={getTransactionStatusColor(value)}>
                    {getTransactionStatus(value)}
                </Status>
            )
        },
        { label: 'Currency', key: 'currency' },
        {
            label: 'Amount',
            key: 'amount',
            render: (value: number, data: TTransactionLite) => (
                <PriceStatus
                    isNegative={data.type === ETransactionType.CREDIT}
                    amount={value + data.fee}
                    currency="$"
                />
            )
        },
        {
            label: 'Actions',
            key: 'id',
            render: (value: string, data: TTransactionLite) => (
                <Dropdown
                    items={[
                        {
                            label: 'Details',
                            onClick: () => {
                                setIsTransactionDrawerOpen(true);
                                getSingleTransaction(value);
                            }
                        },
                        canBeRefunded(data) && {
                            label: 'Refund',
                            onClick: () => {
                                handleOpenRefundModal(value);
                            }
                        }
                    ]}
                />
            )
        }
    ];

    useEffect(() => {
        setRequestSearch(search);
    }, [search]);

    useEffect(() => {
        if (requestSearch.length >= 3) {
            onChangeFilter({ search });
        } else {
            onChangeFilter({ search: undefined });
        }
    }, [requestSearch]);

    const handleChangeDateType = (value: string) => {
        if (dateFilter === value) {
            setDateFilter('');
            onChangeFilter({
                date_from: undefined,
                date_to: undefined
            });
        } else {
            setDateFilter(value);
        }
        if (value === 'week') {
            const date = new Date();
            date.setDate(date.getDate() - 7);
            onChangeFilter({
                date_from: date,
                date_to: new Date()
            });
        } else if (value === 'month') {
            const date = new Date();
            date.setMonth(date.getMonth() - 1);
            onChangeFilter({
                date_from: date,
                date_to: new Date()
            });
        } else if (value === 'year') {
            const date = new Date();
            date.setFullYear(date.getFullYear() - 1);
            onChangeFilter({
                date_from: date,
                date_to: new Date()
            });
        }
    };

    const handleDownloadTransaction = () => {
        const formatTransaction = transactions.map((item) => {
            return {
                'Transaction ID': item.id,
                'Reference code': item.reference_code,
                Action: getTransactionAction(item.action),
                Amount: item.amount,
                Currency: item.currency,
                'Date ': moment(item.created_at).format('MM/DD/YYYY HH:mm:ss'),
                Method: getTransactionMethod(item?.method),
                Status: getTransactionStatus(item?.status),
                'Is Automatic': item.automatic ? 'Yes' : 'No',
                'Consumer Name': item.consumer_name,
                'Consumer Email': item.consumer_email
            };
        });
        downloadFileFromBlob(formatDataToXlsx(formatTransaction), 'all-transactions.xlsx');
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <div className={styles.text_wrapper}>
                    <Typography variant="h4">Transactions</Typography>
                    <Typography>{transactionsData?.total} records found</Typography>
                </div>
                <div className={styles.actions}>
                    <RangeDatePicker
                        values={[
                            filter.date_from?.toString() || null,
                            filter.date_to?.toString() || null
                        ]}
                        maxDate={getDisableFeature()}
                        onChange={(val) =>
                            onChangeFilter({
                                date_from: val?.[0] ? new Date(val?.[0]) : undefined,
                                date_to: val?.[1] ? new Date(val?.[1]) : undefined
                            })
                        }
                    />
                    <Button
                        size="medium"
                        variant="outlined"
                        leftIcon="download"
                        onClick={handleDownloadTransaction}>
                        Download
                    </Button>
                    <Button
                        size="medium"
                        variant="primary"
                        leftIcon="filter_bars"
                        onClick={() => setIsFilterDrawerOpen(true)}>
                        Filter
                    </Button>
                </div>
            </div>
            <Table
                columns={columns}
                rows={transactions}
                sort={{ key: 'created_at', value: filter.order }}
                onSortChange={() => {
                    onChangeFilter({
                        order: filter.order === 'DESC' ? 'ASC' : 'DESC'
                    });
                }}
                filter={filter as TFilter}
                onLoadData={() => refetchTransactions({ isNew: false, filter })}
                totalRows={transactionsData?.total || 0}
                onFilterChange={(val) => onChangeFilter(val)}
                emptyDescription="Transactions list is empty"
            />
            <Drawer
                className={styles.drawer}
                isOpen={isFilterDrawerOpen}
                onClose={() => setIsFilterDrawerOpen(false)}>
                <Input
                    full
                    leftIcon="search"
                    value={search}
                    onChange={(val) => setSearch(val)}
                    placeholder="Search by transaction ID"
                />
                <Accordion label="Date and Time" isDefaultOpened>
                    <div className={styles.date_accordion}>
                        <Button
                            variant={dateFilter === 'week' ? 'primary' : 'outlined'}
                            size="small"
                            onClick={() => handleChangeDateType('week')}>
                            Last week
                        </Button>
                        <Button
                            variant={dateFilter === 'month' ? 'primary' : 'outlined'}
                            size="small"
                            onClick={() => handleChangeDateType('month')}>
                            Last month
                        </Button>
                        <Button
                            variant={dateFilter === 'year' ? 'primary' : 'outlined'}
                            size="small"
                            onClick={() => handleChangeDateType('year')}>
                            Last year
                        </Button>
                    </div>
                    <RangeDatePicker
                        full
                        values={[
                            filter.date_from?.toString() || null,
                            filter.date_to?.toString() || null
                        ]}
                        maxDate={getDisableFeature()}
                        onChange={(val) =>
                            onChangeFilter({
                                date_from: val?.[0] ? new Date(val?.[0]) : undefined,
                                date_to: val?.[1] ? new Date(val?.[1]) : undefined
                            })
                        }
                    />
                </Accordion>
                <Accordion label="Transaction Method" isDefaultOpened>
                    <MultiSelect
                        full
                        values={filter.method || []}
                        onChange={(val) => {
                            onChangeFilter({ method: val as ETransactionMethod[] });
                        }}
                        items={transactionsMethods}
                    />
                </Accordion>
                <Accordion label="Action" isDefaultOpened>
                    <MultiSelect
                        full
                        values={filter.action || []}
                        onChange={(val) => {
                            onChangeFilter({ action: val as ETransactionAction[] });
                        }}
                        items={transactionsType}
                    />
                </Accordion>
                <Accordion label="Status" isDefaultOpened>
                    <MultiSelect
                        full
                        values={filter.status || []}
                        onChange={(val) => {
                            onChangeFilter({ status: val as ETransactionStatus[] });
                        }}
                        items={transactionsStatuses}
                    />
                </Accordion>
                <Accordion label="Amount" isDefaultOpened>
                    <Range
                        min={0}
                        max={1000}
                        step={1}
                        values={[rangeValue.amount_from || 0, rangeValue.amount_to || 1000]}
                        onChange={(val) => {
                            setRangeValue({
                                amount_from: val?.[0],
                                amount_to: val?.[1]
                            });
                        }}
                    />
                </Accordion>
            </Drawer>
            <Drawer
                className={styles.transaction_drawer}
                isOpen={isTransactionDrawerOpen && !!transaction}
                onClose={() => {
                    setIsTransactionDrawerOpen(false);
                    handleRemoveTransactionData();
                }}>
                <Typography className={styles.title} variant="h5">
                    Transaction details
                </Typography>
                <Input full value={transaction?.id} label="Transaction ID" readOnly />
                <Input
                    full
                    value={transaction?.reference_code ?? '—'}
                    label="Reference code"
                    readOnly
                />
                <div className={styles.row}>
                    <Input full value={transaction?.consumer_id} label="Consumer ID" readOnly />
                    <Input full value={transaction?.consumer_name} label="Consumer Name" readOnly />
                </div>
                <div className={styles.row}>
                    <Input
                        full
                        value={getTransactionMethod(transaction?.method)}
                        label="Method"
                        readOnly
                    />
                    <Input
                        full
                        value={getTransactionAction(transaction?.action)}
                        label="Action"
                        readOnly
                    />
                    <Input
                        full
                        value={getTransactionStatus(transaction?.status)}
                        label="Status"
                        readOnly
                    />
                </div>
                <div className={styles.row}>
                    <Input full value={transaction?.currency} label="Currency" readOnly />
                    <Input full value={transaction?.amount?.toFixed(2)} label="Amount" readOnly />
                </div>
                <Input
                    full
                    value={transaction?.automatic ? 'Yes' : 'No'}
                    label="Is automatic payment"
                    readOnly
                />
                <div className={styles.row}>
                    <Input
                        full
                        //@ts-ignore
                        value={transaction?.details?.message || '-'}
                        label="Message"
                        readOnly
                    />
                    <Input full value={transaction?.destination} label="Destination" readOnly />
                </div>
                <div className={styles.row}>
                    <Input full value={transaction?.source} label="Source" readOnly />
                    <Input
                        label="Created at"
                        full
                        value={moment(transaction?.created_at).format('MM.DD.YYYY HH:mm') as string}
                        readOnly
                    />
                </div>
            </Drawer>
        </div>
    );
};

export default TransactionsLayout;
